import { useState } from 'react';
import { localeUI } from '../lib/format';
import { AlarmClock, CalendarDays, MessageSquarePlus, Timer, X } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { Avatar } from './Avatar';
import { ClaimChat } from './ClaimChat';
import { daysOverdue, followUpState, FOLLOWUP_COLOR } from '../lib/followUpColor';

/**
 * EL SEGUIMIENTO DE LA FILA, EN PALABRAS — pedido de Manuel (2026-08-19): "eso que dice 50d
 * está confuso, 50 días de qué? A la derecha tienes bastante espacio: ponme cuántos días tiene
 * en ese status, cuántos días sin follow up, el último follow up con la fecha y quién lo hizo
 * con el avatar, y el next follow up date… y un botón que diga follow up y abra la ventana del
 * department update".
 *
 * El problema del chip viejo no era el número sino que no decía de QUÉ era: tres chips distintos
 * mostraban "50d" y había que adivinar. Acá cada número lleva su etiqueta encima, en columnas.
 *
 * El botón abre el Department Update del claim en una ventana: el follow-up se escribe y se
 * registra ahí mismo (el chat ya tiene el botón que lo marca), sin salir de la cola.
 */
export type FollowUpInfo = {
  claim_id: string;
  status_since?: string | null;
  next_follow_up_at?: string | null;
  /** El próximo EFECTIVO (agendado o calculado, igual que el claim abierto). Ver backend:
   *  `proximoFollowUp`. Si no viene, se cae al agendado — que es lo que mostraba "—". */
  next_follow_up_calc?: string | null;
  last_follow_up_at?: string | null;
  last_follow_up_by?: string | null;
  last_follow_up_by_id?: string | null;
};

/** Días sin follow-up tolerados antes del rojo: al CUARTO ya es rojo (pedido 2026-08-20). */
export const DIAS_SIN_FU_ROJO = 3;

/** Días sin follow-up de una fila — la misma cuenta que muestra el panel, para poder ORDENAR
 *  la cola por ella (el que más días lleva, primero). Sin follow-up nunca: desde que entró. */
export function diasSinFollowUp(r: FollowUpInfo): number {
  return dias(r.last_follow_up_at ?? r.status_since) ?? -1;
}

const dias = (iso?: string | null): number | null =>
  (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);
const dia = (iso?: string | null): string => (iso ? new Date(iso).toLocaleDateString(localeUI()) : '—');

export function FollowUpPanel({ r, deptKey, onChanged, compact = false, vertical = false }: {
  r: FollowUpInfo; deptKey?: string; onChanged?: () => void; compact?: boolean;
  /** En columna, pegado a la derecha de la tarjeta (pedido de Manuel 2026-08-20: "lo quiero a
   *  la derecha vertical pegado a la derecha, que tengo mucho espacio en blanco por allá").
   *  Horizontal abajo desperdiciaba el ancho de la fila y empujaba el resto hacia abajo. */
  vertical?: boolean;
}) {
  const { t } = useI18n();
  const [abierto, setAbierto] = useState(false);
  const enStatus = dias(r.status_since);
  // Sin follow-up: desde el último; si nunca hubo, desde que llegó a la cola.
  const sinFu = dias(r.last_follow_up_at ?? r.status_since);
  const nunca = !r.last_follow_up_at;
  // El próximo EFECTIVO: el agendado a mano si existe, si no el calculado por el backend con la
  // misma fórmula del claim abierto. Leer sólo el agendado era lo que ponía "—" en la cola.
  const proximo = r.next_follow_up_calc ?? r.next_follow_up_at ?? null;
  const estado = followUpState(proximo);
  const col = FOLLOWUP_COLOR[estado];
  /**
   * ROJO AL CUARTO DÍA SIN FOLLOW-UP — regla de Manuel (2026-08-20): "después del 3 days sin
   * follow up ya el 4to lo pone en rojo". Es sobre los días SIN follow-up, no sobre la fecha
   * del próximo: un claim puede tener el próximo al día y llevar una semana sin que nadie
   * escriba. Se suma al semáforo, no lo reemplaza (vencido también pinta rojo).
   */
  const urgente = (sinFu != null && sinFu > DIAS_SIN_FU_ROJO) || estado === 'late' || estado === 'manager';

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}
        style={vertical
          ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, minWidth: 150, textAlign: 'right' }
          : { display: 'flex', alignItems: 'stretch', gap: compact ? 10 : 14, flexWrap: 'wrap' }}>
        <Dato icon={<Timer size={13} strokeWidth={2} />} label={t('fup.inStatus')} valor={enStatus != null ? `${enStatus}d` : '—'} derecha={vertical} chico={vertical} />
        <Dato
          icon={<AlarmClock size={13} strokeWidth={2} />}
          label={t('fup.noFollowUp')}
          valor={sinFu != null ? `${sinFu}d` : '—'}
          color={urgente ? 'var(--red)' : undefined}
          nota={nunca ? t('fup.never') : undefined}
          derecha={vertical}
          chico={vertical}
        />
        <Dato
          icon={<CalendarDays size={13} strokeWidth={2} />}
          label={t('fup.lastFollowUp')}
          valorNodo={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{dia(r.last_follow_up_at)}</span>
              {(r.last_follow_up_by_id || r.last_follow_up_by) && (
                <Avatar userId={r.last_follow_up_by_id ?? null} name={r.last_follow_up_by ?? null} size={vertical ? 15 : 18} />
              )}
            </span>
          }
          nota={r.last_follow_up_by ?? undefined}
          derecha={vertical}
          chico={vertical}
        />
        <Dato
          icon={<AlarmClock size={13} strokeWidth={2} />}
          label={t('fup.next')}
          valor={dia(proximo)}
          color={col.fg}
          fondo={col.bg}
          derecha={vertical}
          chico={vertical}
        />
        <button className="btn sm" onClick={() => setAbierto(true)} title={t('fup.btnHint')}
          style={{ alignSelf: vertical ? 'stretch' : 'center', fontWeight: 700, whiteSpace: 'nowrap', ...(vertical ? { marginTop: 2, padding: '3px 10px' } : {}) }}>
          <MessageSquarePlus size={14} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {t('fup.btn')}
        </button>
      </div>

      {abierto && <ModalDeFollowUp claimId={r.claim_id} deptKey={deptKey} onClose={() => { setAbierto(false); onChanged?.(); }} />}
    </>
  );
}

/** Una columna: etiqueta chica arriba, número grande abajo. El número nunca va solo. */
function Dato({ icon, label, valor, valorNodo, color, fondo, nota, derecha = false, chico = false }: {
  icon: React.ReactNode; label: string; valor?: string; valorNodo?: React.ReactNode;
  color?: string; fondo?: string; nota?: string; derecha?: boolean; chico?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 0, padding: fondo ? '2px 8px' : (chico ? '1px 0' : '3px 0'), borderRadius: 9, background: fondo, justifyItems: derecha ? 'end' : undefined, textAlign: derecha ? 'right' : undefined }}>
      <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], textTransform: 'uppercase', letterSpacing: 0.4, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
        {icon} {label}
      </span>
      <span style={{ fontSize: chico ? 'var(--fs-sm)' : 'var(--fs-base)', fontWeight: 800, color: color ?? 'var(--ink)', fontFamily: valorNodo ? undefined : 'var(--font-mono)', lineHeight: 1.1 }}>
        {valorNodo ?? valor}
      </span>
      {nota && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', lineHeight: 1.1, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nota}</span>}
    </div>
  );
}

/** La ventana del Department Update desde la cola (la comparten el panel y la fila). */
function ModalDeFollowUp({ claimId, deptKey, onClose }: { claimId: string; deptKey?: string; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <div onClick={onClose} role="presentation"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 4000, display: 'grid', placeItems: 'start center', padding: '5vh 16px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: 780, display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <b style={{ flex: 1, fontSize: 'var(--fs-sm)' }}>{t('fup.modalTitle')}</b>
          <button className="btn ghost sm" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>
        {/* El chat del depto ya trae el botón que marca el mensaje como follow-up y re-arma el reloj. */}
        <ClaimChat claimId={claimId} channel={deptKey || 'claims'} title={t('fup.modalTitle')} />
      </div>
    </div>
  );
}

/**
 * LA FILA DEL BOARD DE SUPPLEMENTS (dueño 2026-10-29): "a la derecha hay un número en rojo, 85 días, ¿qué
 * significa? ... quiero que diga cuánto tiempo tienen total, cuántos días de su último follow-up y cuándo es
 * el próximo, en verde si está bien, amarillo si está por vencerse y rojo si ya está expired; los muchachos
 * no entienden". El board de Supplements nunca adoptó el panel de arriba (2026-08-19): mostraba "85d" sin
 * decir de qué, "Last" sin quién y un "Next" agendado a mano que quedaba viejo. Son los MISMOS tres datos
 * del panel, en una línea, cada uno con su etiqueta, y el semáforo único de followUpColor.
 *  · In status: días hábiles (los del board) desde que entró a la columna.
 *  · Last follow-up: hace N días + fecha + avatar; rojo al 4º día sin follow-up (DIAS_SIN_FU_ROJO).
 *  · Next: el EFECTIVO (agendado a mano o calculado por la política del menú) y cuánto falta / cuánto lleva
 *    vencido; sin reloj cuando el menú de la vista no lleva follow-up.
 */
export function FollowUpInline({ r, deptKey, onChanged, diasHabiles }: {
  r: FollowUpInfo; deptKey?: string; onChanged?: () => void;
  /** Días HÁBILES en el status (los calcula el backend); sin esto se cuentan días de calendario. */
  diasHabiles?: number | null;
}) {
  const { t } = useI18n();
  const [abierto, setAbierto] = useState(false);
  const enStatus = diasHabiles ?? dias(r.status_since);
  const sinFu = dias(r.last_follow_up_at);
  const nunca = !r.last_follow_up_at;
  const proximo = r.next_follow_up_calc ?? r.next_follow_up_at ?? null;
  const estado = followUpState(proximo);
  const col = FOLLOWUP_COLOR[estado];
  const venc = daysOverdue(proximo);
  const cuanto = venc == null ? null : venc > 0 ? t('fu3.late', { d: String(venc) }) : venc === 0 ? t('fu3.today') : t('fu3.in', { d: String(-venc) });
  const rojoSinFu = (sinFu != null && sinFu > DIAS_SIN_FU_ROJO) || (nunca && (enStatus ?? 0) > DIAS_SIN_FU_ROJO);
  const pill: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--ink)', fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap', lineHeight: 1.5 };
  const etiqueta: React.CSSProperties = { textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '10px', fontWeight: 800, opacity: 0.8 };
  const num: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 800 };
  return (
    <>
      <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={pill} title={diasHabiles != null ? t('fup.inStatusHint', { d: dia(r.status_since) }) : `${t('fup.inStatus')} · ${dia(r.status_since)}`}>
          <Timer size={12} strokeWidth={2} /> <span style={etiqueta}>{t('fup.inStatus')}</span>
          <span style={num}>{enStatus != null ? enStatus : '—'}</span><span>{diasHabiles != null ? t('fup.bd') : 'd'}</span>
        </span>
        <span style={{ ...pill, ...(rojoSinFu ? { background: 'var(--red-soft)', color: 'var(--red)', borderColor: 'var(--red)' } : {}) }} title={t('fup.lastHint')}>
          <CalendarDays size={12} strokeWidth={2} /> <span style={etiqueta}>{t('fup.lastFollowUp')}</span>
          {nunca ? <span>{t('fup.never')}</span> : (
            <>
              <span style={num}>{t('fup.agoShort', { d: String(sinFu ?? 0) })}</span>
              <span style={{ opacity: 0.8 }}>· {dia(r.last_follow_up_at)}</span>
              {(r.last_follow_up_by_id || r.last_follow_up_by) && <Avatar userId={r.last_follow_up_by_id ?? null} name={r.last_follow_up_by ?? null} size={14} />}
            </>
          )}
        </span>
        <span style={{ ...pill, background: col.bg, color: col.fg, borderColor: col.border }} title={t('fup.nextHint')}>
          <AlarmClock size={12} strokeWidth={2} /> <span style={etiqueta}>{t('fup.next')}</span>
          {proximo ? <><span style={num}>{dia(proximo)}</span><span>· {cuanto}</span></> : <span>{t('fup.noClock')}</span>}
        </span>
        <button className="btn sm" onClick={() => setAbierto(true)} title={t('fup.btnHint')} style={{ fontWeight: 700, whiteSpace: 'nowrap', padding: '2px 9px' }}>
          <MessageSquarePlus size={13} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {t('fup.btn')}
        </button>
      </span>
      {abierto && <ModalDeFollowUp claimId={r.claim_id} deptKey={deptKey} onClose={() => { setAbierto(false); onChanged?.(); }} />}
    </>
  );
}
